import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { CARDS, boardClock } from "@/data/goalsBoard";
import { useBoard } from "@/hooks/useBoard";
import { GoalsBoard } from "./GoalsBoard";
import { GoalDetail } from "./GoalDetail";
import { Card, Label, StateWord } from "@/design/primitives";
import { AuthGate, StartHere, AddWorkBar } from "@/screens/cc";

// Bare wall, now interactive: live rows from the action API hydrate the board;
// the committed baked data renders as-is whenever the API isn't reachable.
// Jeff-facing rule: every button does the real thing — approve moves the work.

// Idea detail (Jeff 10/30: "I can't click on it, I can't do anything"): the
// full intake record — his original words kept verbatim, the voice transcript,
// Kit's processed refine (retitle/describe/scope/steps), and the one click
// that turns it into queued work. No dead-end badges.
const IdeaDetail = ({ row, board, onClose, onFlash }) => {
  const slug = row.slug;
  const p = row.payload || {};
  const refine = p.kit_refine || null;
  const transcripts = Object.values(p.attachments || {})
    .filter((a) => a && a.kind === "transcript" && a.transcript);
  const queued = row.state === "QUEUED";
  const [pending, setPending] = useState(false);
  const queue = async () => {
    setPending(true);
    const res = await board.act("queueIdea", slug);
    setPending(false);
    if (res) {
      onFlash("Queued — it's in the work queue and Kit starts on it next.");
      onClose();
    }
  };
  return (
    <div
      data-testid={`idea-detail-${slug}`}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-os-ink/40 p-4 md:items-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl rounded-card border border-os-border bg-os-bg p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="min-w-0 text-[1.0625rem] font-semibold tracking-[-0.01em] text-os-ink">
            {p.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md border border-os-border px-2 py-1 font-mono text-[0.75rem] text-os-ink-2 hover:border-os-border-strong"
          >
            Close
          </button>
        </div>
        <div className="mt-2">
          {queued ? <StateWord tone="amber">Queued — work in flight</StateWord>
            : <StateWord tone="green">Kit is on it</StateWord>}
        </div>

        <Label as="p" className="mt-4">Your original words</Label>
        <p className="mt-1 whitespace-pre-wrap text-[0.875rem] leading-[1.45] text-os-ink-2">
          {p.why || "(just the title — nothing else written)"}
          {transcripts.map((t) => (
            <span key={t.task_id}>
              <br />🎙 “{t.transcript}”
            </span>
          ))}
        </p>

        {refine ? (
          <>
            <Label as="p" className="mt-4">
              Processed by Kit{refine.model ? ` · ${refine.model}` : ""}
            </Label>
            <div className="mt-1 rounded-card border border-os-border bg-os-ink/[0.03] p-4" data-testid={`idea-refine-${slug}`}>
              <p className="text-[0.9375rem] font-semibold text-os-ink">{refine.title}</p>
              <p className="mt-1 text-[0.875rem] leading-[1.45] text-os-ink-2">{refine.description}</p>
              {refine.scope && (
                <p className="mt-2 text-[0.8125rem] text-os-ink-3">Scope: {refine.scope}</p>
              )}
              {Array.isArray(refine.steps) && refine.steps.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-[0.8125rem] leading-[1.5] text-os-ink-2">
                  {refine.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              )}
            </div>
          </>
        ) : (
          <Label as="p" className="mt-4 text-os-amber">
            Kit hasn't finished processing this one yet — a moment after pickup.
          </Label>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <p className="text-[0.8125rem] text-os-ink-3">
            {queued ? "It's in the queue — the exec board tracks the run."
              : "Queue it and the work starts from the processed version."}
          </p>
          <button
            type="button"
            data-testid={`idea-queue-${slug}`}
            onClick={queue}
            disabled={pending || queued || !refine}
            className="rounded-md border border-os-border px-3 py-1.5 font-mono text-[0.75rem] text-os-ink hover:border-os-border-strong disabled:opacity-50"
          >
            {queued ? "Queued" : pending ? "Queueing…" : "Queue it up"}
          </button>
        </div>
      </div>
    </div>
  );
};

const IdeaCard = ({ item, onOpen }) => {
  // Attached media (Jeff 10/30): clips and files bound to this idea show on the
  // card — his memo + brief were stored but invisible until this strip existed.
  const [attach, setAttach] = useState([]);
  useEffect(() => {
    let on = true;
    fetch(`/api/idea-attachments?board=goals&slug=${encodeURIComponent(item.slug)}`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => on && setAttach(d.items || []))
      .catch(() => {});
    return () => { on = false; };
  }, [item.slug]);
  const refined = Boolean((item.payload || {}).kit_refine);
  return (
  <Card
    quiet
    data-testid={`goal-card-${item.slug}`}
    data-state={item.state === "PICKED" ? "PICKED" : item.state === "QUEUED" ? "QUEUED" : "INTAKE"}
    onClick={() => onOpen?.(item.slug)}
    className="flex h-full cursor-pointer flex-col p-5 transition hover:border-os-border-strong"
  >
  <div className="flex items-baseline justify-between gap-3">
    <h3 className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">
      {item.payload.title}
    </h3>
    {item.state === "QUEUED"
      ? <StateWord tone="amber">Queued</StateWord>
      : item.state === "PICKED"
        ? <StateWord tone="green">Kit is on it</StateWord>
        : <StateWord tone="muted">New idea</StateWord>}
  </div>
    <Label as="p" className="mt-1">Asked for from this board</Label>
    <p className="mt-3 flex-1 text-[0.875rem] leading-[1.45] text-os-ink-2">
      {item.payload.why || "No extra detail — Kit starts with what's written here."}
    </p>
    {attach.length > 0 && (
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`idea-attach-strip-${item.slug}`}>
        {attach.map((a) => (
          <span key={a.id} className="inline-flex items-center gap-1.5 rounded-md bg-os-ink/5 px-2 py-1 text-[0.75rem] text-os-ink-2">
            {a.kind === "voice" ? "🎙" : "📎"} {a.filename}
          </span>
        ))}
      </div>
    )}
    {refined && (item.payload.kit_refine.title || item.payload.kit_refine.description) ? (
      <p className="mt-3 truncate text-[0.8125rem] font-medium text-os-green" data-testid={`idea-refined-line-${item.slug}`}>
        Processed: {(item.payload.kit_refine.title || item.payload.kit_refine.description).slice(0, 70)} — click to review & queue
      </p>
    ) : item.state === "QUEUED" ? (
      <p className="mt-3 text-[0.8125rem] font-medium text-os-amber">Queued — the work is in flight</p>
    ) : (
      <p className="mt-3 text-[0.8125rem] font-medium text-os-amber">Kit picks this up — click for progress</p>
    )}
  </Card>
  );
};

export default function GoalsBoardPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const clock = useMemo(() => boardClock(), []);
  const board = useBoard("goals");
  const [openSlug, setOpenSlug] = useState(null);
  const [detailSlug, setDetailSlug] = useState(null);
  const [flash, setFlash] = useState(null);

  const liveItems = board.items || [];
  const liveGoals = liveItems.filter((r) => r.kind === "goal");
  // Queued ideas stay on the board until the work lands — they show their state
  // instead of vanishing the moment they're queued.
  const liveIdeas = liveItems.filter(
    (r) => r.kind === "idea" && r.state !== "ARCHIVED" && r.state !== "QUEUED",
  );
  const queuedIdeas = liveItems.filter((r) => r.kind === "idea" && r.state === "QUEUED");

  const cards = useMemo(() => {
    if (!board.live) return CARDS; // baked committed data (also what signed-out eyes see)
    return [...liveGoals]
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map((r) => {
        const p = r.payload || {};
        const approved = (p.jeff || {}).approved;
        return {
          ...p,
          slug: r.slug,
          rawState: r.state,
          stateWord: approved ? "Approved — wrapping up" : p.stateWord,
          next: approved
            ? "Next: Kit finishes the wrap-up — archive pass, notes, and the work ledger"
            : p.next,
          blockersLine: approved ? "Nothing waiting on you anymore" : p.blockersLine,
        };
      });
  }, [board.live, liveGoals]);

  // Start here: the first card actually waiting on Jeff's approval right now.
  const waiting = liveItems.filter(
    (r) => r.kind === "goal" && (r.state === "TEST" || r.state === "PLAN") && !(r.payload?.jeff || {}).approved,
  );
  const firstWait = waiting[0];
  const startHere = !board.live
    ? {
        tone: "muted",
        text: board.status === "needs-signin"
          ? "Sign in to work the board — your buttons appear the moment you're in"
          : "Actions are offline this visit — the board still shows the latest committed state",
      }
    : firstWait
      ? {
          tone: "amber",
          text: `Approve the ${firstWait.payload?.title || "next piece of work"} — one click and the work moves`,
          button: {
            label: board.pending === firstWait.slug ? "Moving it…" : "Approve",
            disabled: Boolean(board.pending),
            onClick: async () => {
              const res = await board.act("approve", firstWait.slug);
              if (res) setFlash("Approved — it's moving. Kit finishes the wrap-up from here.");
            },
          },
        }
      : liveIdeas.length
        ? { tone: "muted", text: "Nothing waits on your approval — Kit is executing. New ideas welcome below." }
        : { tone: "green", text: "All clear — nothing needs you here right now." };

  const openItem = board.live
    ? (() => {
        const row = liveItems.find((r) => r.slug === openSlug);
        if (!row) return null;
        if (row.kind === "idea") return null;
        const p = row.payload || {};
        const approved = (p.jeff || {}).approved;
        return {
          ...p,
          slug: row.slug,
          rawState: row.state,
          stateWord: approved ? "Approved — wrapping up" : p.stateWord,
        };
      })()
    : null;

  const detailRow = board.live
    ? liveItems.find((r) => r.slug === detailSlug && r.kind === "idea") || null
    : null;

  const close = () => setOpenSlug(null);

  const renderBoard = (children) => (
    <div data-testid="goals-live-page" className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        {children}
      </div>
    </div>
  );

  // Signed out: gate first, board below it so Jeff can still look while signing in.
  if (board.status === "needs-signin") {
    return renderBoard(
      <>
        <AuthGate />
        <GoalsBoard cards={CARDS} clock={clock} variant={desktop ? "desktop" : "mobile"} board={board} />
      </>,
    );
  }

  return renderBoard(
    <>
      <StartHere {...startHere} testId="goals-start-here" />
      {flash && (
        <p data-testid="goals-flash" className="text-[0.875rem] font-medium text-os-green">
          {flash}
        </p>
      )}
      {board.live ? (
        <>
          <GoalsBoard cards={cards} clock={clock} variant={desktop ? "desktop" : "mobile"} board={board} />
          {liveIdeas.length > 0 && (
            <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
              {liveIdeas.map((r) => (
                <IdeaCard key={r.slug} item={r} onOpen={setDetailSlug} />
              ))}
            </div>
          )}
          {queuedIdeas.length > 0 && (
            <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
              {queuedIdeas.map((r) => (
                <IdeaCard key={r.slug} item={r} onOpen={setDetailSlug} />
              ))}
            </div>
          )}
          <AddWorkBar />
        </>
      ) : (
        <>
          <GoalsBoard cards={CARDS} clock={clock} variant={desktop ? "desktop" : "mobile"} board={board} />
          <p className="px-1 font-mono text-[0.6875rem] text-os-ink-3">
            Actions are offline this visit — the board shows the latest committed state.
          </p>
        </>
      )}
      {openItem && (
        <GoalDetail
          card={openItem}
          variant={desktop ? "desktop" : "mobile"}
          onClose={close}
          editor={board.live ? { board, item: liveItems.find((r) => r.slug === openSlug) } : null}
        />
      )}
      {detailRow && (
        <IdeaDetail
          row={detailRow}
          board={board}
          onClose={() => setDetailSlug(null)}
          onFlash={setFlash}
        />
      )}
    </>,
  );
}